import React, { useState, useRef } from 'react';
import Webcam from 'react-webcam';
import { useNavigate } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { IoClose } from 'react-icons/io5';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import Cookies from 'js-cookie';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const getGuestIdFromToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub;
  } catch (e) {
    console.error('Error parsing token:', e);
    return null;
  }
};

const decryptUserData = async (encryptedData) => {
  try {
    if (encryptedData.email && encryptedData.email.length < 50) {
      return encryptedData;
    }

    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        encrypted_fields: {
          email: encryptedData.email,
          phone_number: encryptedData.phone_number,
          country_code: encryptedData.country_code,
        },
      }),
    });

    if (response.status === 401) {
      return encryptedData;
    }

    if (!response.ok) {
      throw new Error(`Decryption failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Decryption error:', error);
    return encryptedData;
  }
};

const getGuestData = async () => {
  const accessToken = Cookies.get('access_token');
  if (!accessToken) return null;

  try {
    const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
    if (!guestId) return null;

    const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!profileResponse.ok) {
      throw new Error(`Profile API Error: ${profileResponse.statusText}`);
    }

    const profileData = await profileResponse.json();
    const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

    if (isDecrypted) {
      return {
        firstname: profileData.firstname || '',
        lastname: profileData.lastname || '',
        email: profileData.emailid || '',
        phone: profileData.phonenumber || '',
      };
    }

    const decryptedFields = await decryptUserData({
      email: profileData.emailid,
      phone_number: profileData.phonenumber,
      country_code: profileData.countrycode,
    });

    return {
      firstname: profileData.firstname || '',
      lastname: profileData.lastname || '',
      email: decryptedFields.email || profileData.emailid || '',
      phone: decryptedFields.phone_number || profileData.phonenumber || '',
    };
  } catch (error) {
    console.error('Error fetching guest data:', error);
    return null;
  }
};

// Helper function to categorize errors
const categorizeError = (errorMessage) => {
  if (errorMessage && typeof errorMessage === 'string') {
    if (
      errorMessage.includes('No active bookings found') ||
      errorMessage.includes('No active reservations')
    ) {
      return 'NO_RESERVATIONS';
    } else if (
      errorMessage.includes('not enrolled') ||
      errorMessage.includes('Face not recognized')
    ) {
      return 'FACE_NOT_ENROLLED';
    } else if (
      errorMessage.includes('Face not detected') ||
      errorMessage.includes('No face detected')
    ) {
      return 'NO_FACE_DETECTED';
    } else if (errorMessage.includes('different account')) {
      return 'WRONG_ACCOUNT';
    } else if (errorMessage.includes('Network error')) {
      return 'NETWORK_ERROR';
    }
  }
  return 'GENERIC_ERROR';
};

const SearchReservation = () => {
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [similarity, setSimilarity] = useState(null);
  const [guestId, setGuestId] = useState(null);
  const [guestDetails, setGuestDetails] = useState(null);
  const [bookingData, setBookingData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState('GENERIC_ERROR');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [earlyCheckinData, setEarlyCheckinData] = useState(null);
  const [showPreCheckinErrorModal, setShowPreCheckinErrorModal] = useState(false);
  const [preCheckinErrorMessage, setPreCheckinErrorMessage] = useState('');
  const [hotelInfo, setHotelInfo] = useState({ name: '', email: '' });
  const webcamRef = useRef(null);
  const navigate = useNavigate();

  const shouldShowDisclaimer = (checkInDate) => {
    const now = new Date();
    const checkInDateTime = new Date(checkInDate);

    // Set check-in time to 11:00 AM
    checkInDateTime.setHours(11, 0, 0, 0);

    // Calculate 2 hours prior to check-in time (11:00 AM)
    const twoHoursPrior = new Date(checkInDateTime);
    twoHoursPrior.setHours(twoHoursPrior.getHours() - 2);

    // If current time is before the 2 hours prior to check-in, show disclaimer
    return now < twoHoursPrior;
  };

  const captureImage = () => {
    if (webcamRef.current && webcamRef.current.getScreenshot) {
      const imageSrc = webcamRef.current.getScreenshot();
      setPreview(imageSrc);
      handleCheckIn(imageSrc);
    } else {
      setErrorMessage('Failed to capture image. Please try again.');
      setErrorType('GENERIC_ERROR');
      setShowErrorModal(true);
    }
  };

  const validateFaceMatch = async (faceRecognitionData) => {
    try {
      // Get current user's data
      const currentUserData = await getGuestData();
      if (!currentUserData) {
        throw new Error('Unable to verify your account. Please log in again.');
      }

      // Get email from face recognition result
      const recognizedEmail = faceRecognitionData.bookings?.[0]?.guest_email;
      if (!recognizedEmail) {
        throw new Error('No email found in recognition data.');
      }

      // Compare emails
      if (currentUserData.email.toLowerCase() !== recognizedEmail.toLowerCase()) {
        throw new Error(
          'Face recognition matched a different account. Please use the account that made this reservation.'
        );
      }

      return true;
    } catch (error) {
      console.error('Validation error:', error);
      throw error; // Re-throw to handle in calling function
    }
  };

  const handleCheckIn = async (imageData) => {
    try {
      const base64Response = await fetch(imageData);
      const blob = await base64Response.blob();
      const formData = new FormData();
      formData.append('file', blob, 'captured_image.jpg');

      const response = await fetch(`${BQ_BASE_URL}/bq/api/checkin`, {
        method: 'POST',
        headers: { accept: 'application/json' },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.bookings && data.bookings.length > 0) {
          // Validate the recognized face matches logged-in user
          await validateFaceMatch(data);

          // Extract guest details from the first booking
          const firstBooking = data.bookings[0];
          const guestDetails = {
            firstname: firstBooking.guest_name.split(' ')[0],
            lastname: firstBooking.guest_name.split(' ').slice(1).join(' '),
            emailid: firstBooking.guest_email,
            phonenumber: firstBooking.guest_phone,
          };

          setMessage(data.message);
          setGuestDetails(guestDetails);
          setBookingData(data.bookings);
          setShowModal(true);
        } else {
          // Handle case where no bookings are found
          if (data.detail === 'No active bookings found.') {
            const errorMsg =
              'No active reservations found. Please make sure you have a confirmed booking or contact reception for assistance.';
            setErrorMessage(errorMsg);
            setErrorType(categorizeError(errorMsg));
          } else {
            const errorMsg = data.message || 'No booking data found. Please try again.';
            setErrorMessage(errorMsg);
            setErrorType(categorizeError(errorMsg));
          }
          setShowErrorModal(true);
        }
      } else {
        const errorData = await response.json();

        // Determine error message based on response
        let errorMsg;
        if (errorData.detail === 'No active bookings found.') {
          errorMsg =
            'No active reservations found. Please make sure you have a confirmed reservation or contact reception for assistance.';
        } else if (
          errorData.detail === 'Face not recognized.' ||
          errorData.detail === 'No face detected.'
        ) {
          errorMsg =
            'Face recognition failed. Please ensure your face is clearly visible and try again. If the issue persists, please contact reception.';
        } else if (
          errorData.message?.includes('not enrolled') ||
          errorData.detail?.includes('not enrolled')
        ) {
          errorMsg =
            'Face not enrolled. Please register your face in the Profile section first before attempting face recognition.';
        } else {
          errorMsg =
            errorData.message || errorData.detail || 'Face verification failed. Please try again.';
        }

        setErrorMessage(errorMsg);
        setErrorType(categorizeError(errorMsg));
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error:', error);

      // Handle network or other errors
      let errorMsg;
      if (error.message.includes('Network')) {
        errorMsg = 'Network error. Please check your internet connection and try again.';
      } else if (error.message.includes('logged in')) {
        errorMsg = 'Please log in to your account first before using face recognition.';
      } else {
        errorMsg = error.message || 'An unexpected error occurred. Please try again.';
      }

      setErrorMessage(errorMsg);
      setErrorType(categorizeError(errorMsg));
      setShowErrorModal(true);
    }
  };

  const proceedWithCheckIn = async (booking) => {
    setProcessing(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/pre-checkin/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: booking.order_id,
          last_name: guestDetails.lastname,
          email: guestDetails.emailid,
        }),
      });

      if (response.ok) {
        const data = await response.json();

        // Check if the API response indicates failure
        if (data.success === false) {
          setPreCheckinErrorMessage(data.message || 'Check-in failed. Please try again.');

          // Get hotel info from localStorage
          const hotelPropertyCache = JSON.parse(localStorage.getItem('hotelPropertyCache') || '{}');
          if (hotelPropertyCache.data) {
            setHotelInfo({
              name:
                hotelPropertyCache.data.propertyname || hotelPropertyCache.data.name || 'The Hotel',
              email: hotelPropertyCache.data.email || 'support@hotel.com',
            });
          }

          setShowPreCheckinErrorModal(true);
          setProcessing(false);
          return;
        }

        // Prepare the full booking details object in the required format
        // Maintain the original structure that other components expect
        const bookingDetails = {
          success: true,
          message: data.message,
          today: new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
          order_id: data.order_id,
          primary_guest: data.primary_guest,
          bookings: data.bookings,
          multiple_rooms: data.multiple_rooms,
          attention_needed: data.attention_needed,
          primaryBooking: data.bookings[0], // Use the first booking from API response
        };

        // Store the hotel property cache
        const hotelPropertyCache = {
          data: {
            propertyname: data.bookings[0].room_type.property.name,
            city: 'Chiplun',
            name: data.bookings[0].room_type.property.name,
            location: 'Taluka Khed Chiplun Area',
            address: 'Taluka Khed Chiplun Area, Lote, Maharashtra, 415722',
            phone: '086987 32336',
            email: 'tushar.bhosle@hotelpagoda.com',
            propertyid: data.bookings[0].room_type.property.id,
            logo_url:
              'https://storage.googleapis.com/hms-image-storage/property-logos/1/176327d44-a199-41b1-9c66.png',
            homepage_url:
              'https://storage.googleapis.com/hms-image-storage/property-homepage/1/f102d3be-1d32-48c6-9f89-a201c0c101b5.jpg',
            homepage_video_url: '',
          },
          timestamp: Date.now(),
        };

        // Save to storage
        localStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));
        sessionStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));
        localStorage.setItem('hotelPropertyCache', JSON.stringify(hotelPropertyCache));

        // Navigate based on booking status
        if (data.bookings[0].booking_status.toLowerCase() === 'soft') {
          navigate('/checkin-details');
        } else {
          navigate('/reservation/options/booking/welcome');
        }
      } else {
        const errorData = await response.json();
        setPreCheckinErrorMessage(
          errorData.message || errorData.detail?.message || 'Check-in failed. Please try again.'
        );

        // Get hotel info from localStorage
        const hotelPropertyCache = JSON.parse(localStorage.getItem('hotelPropertyCache') || '{}');
        if (hotelPropertyCache.data) {
          setHotelInfo({
            name:
              hotelPropertyCache.data.propertyname || hotelPropertyCache.data.name || 'The Hotel',
            email: hotelPropertyCache.data.email || 'support@hotel.com',
          });
        }

        setShowPreCheckinErrorModal(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setPreCheckinErrorMessage(error.message || 'An unexpected error occurred. Please try again.');

      // Get hotel info from localStorage
      const hotelPropertyCache = JSON.parse(localStorage.getItem('hotelPropertyCache') || '{}');
      if (hotelPropertyCache.data) {
        setHotelInfo({
          name: hotelPropertyCache.data.propertyname || hotelPropertyCache.data.name || 'The Hotel',
          email: hotelPropertyCache.data.email || 'support@hotel.com',
        });
      }

      setShowPreCheckinErrorModal(true);
    } finally {
      setProcessing(false);
      setShowDisclaimer(false);
    }
  };

  const handleProceed = async (booking) => {
    setSelectedBooking(booking);

    // Check if already checked in - don't show disclaimer for checked-in guests
    if (booking.booking_status.toLowerCase() === 'hard') {
      // Skip disclaimer and proceed directly for already checked-in guests
      proceedWithCheckIn(booking);
      return;
    }

    // Check if we need to show the early check-in disclaimer
    const today = new Date().toISOString().split('T')[0];
    if (booking.checkin_date === today && shouldShowDisclaimer(booking.checkin_date)) {
      try {
        // Fetch early check-in details
        const response = await fetch(
          `${BQ_BASE_URL}/bq/api/pre-checkin/?order_id=${booking.order_id}&last_name=${guestDetails.lastname}`,
          {
            method: 'GET',
            headers: {
              accept: 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          // Extract early check-in data from the first booking
          if (data.bookings && data.bookings.length > 0) {
            setEarlyCheckinData(data.bookings[0].early_checkin);
          }
        }
      } catch (error) {
        console.error('Error fetching early check-in details:', error);
      }

      setShowDisclaimer(true);
    } else {
      // Proceed directly if no disclaimer needed
      proceedWithCheckIn(booking);
    }
  };

  const resetForm = () => {
    setPreview(null);
    setMessage('');
    setSimilarity(null);
    setGuestId(null);
    setGuestDetails(null);
    setBookingData([]);
    setShowModal(false);
    setShowErrorModal(false);
    setShowDisclaimer(false);
    setEarlyCheckinData(null);
    setShowPreCheckinErrorModal(false);
    setPreCheckinErrorMessage('');
    setErrorMessage('');
    setErrorType('GENERIC_ERROR');
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  // Get modal title based on error type
  const getErrorTitle = () => {
    switch (errorType) {
      case 'NO_RESERVATIONS':
        return 'No Reservations Found';
      case 'FACE_NOT_ENROLLED':
        return 'Face Not Registered';
      case 'NO_FACE_DETECTED':
        return 'No Face Detected';
      case 'WRONG_ACCOUNT':
        return 'Account Mismatch';
      case 'NETWORK_ERROR':
        return 'Connection Error';
      default:
        return 'Verification Failed';
    }
  };

  // Get icon based on error type
  const getErrorIcon = () => {
    switch (errorType) {
      case 'NO_RESERVATIONS':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 text-black"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case 'FACE_NOT_ENROLLED':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 text-orange-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        );
      default:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
    }
  };

  // Get button color based on error type
  const getButtonColor = () => {
    switch (errorType) {
      case 'NO_RESERVATIONS':
        return 'bg-blue-500 hover:bg-blue-600';
      case 'FACE_NOT_ENROLLED':
        return 'bg-orange-500 hover:bg-orange-600';
      default:
        return 'bg-red-500 hover:bg-red-600';
    }
  };

  // Get secondary button color
  const getSecondaryButtonColor = () => {
    switch (errorType) {
      case 'FACE_NOT_ENROLLED':
        return 'bg-blue-500 hover:bg-blue-600';
      default:
        return 'bg-gray-200 text-gray-800 hover:bg-gray-300';
    }
  };

  return (
    <div className="min-h-1 flex flex-col items-center p-6 bg-gray-50">
      <div className="w-full max-w-6xl">
        <h1 className="text-2xl font-poppins font-bold text-center text-gray-800 mb-4">
          Search Reservation
        </h1>
        <p className="text-center font-poppins text-gray-600 mb-8 text-lg font-semibold">
          Scan your face to retrieve your reservation details
        </p>

        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between">
          {/* Webcam and Capture Section */}
          <div className="w-full lg:w-1/2 flex flex-col items-center mb-8 lg:mb-0">
            <div className="bg-white p-4 rounded-lg shadow-lg">
              {preview ? (
                <img src={preview} alt="Preview" className="rounded-lg w-96 h-80 object-cover" />
              ) : (
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  className="rounded-lg w-96 h-80 object-cover"
                />
              )}
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={captureImage}
                className="px-8 py-3 font-poppins bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition duration-300"
              >
                Capture Face
              </button>
              <button
                onClick={resetForm}
                className="px-8 py-3 font-poppins bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition duration-300"
              >
                Try Again
              </button>
            </div>
          </div>

          {/* Instructions Section */}
          {!showModal && !showErrorModal && (
            <div className="w-full lg:w-2/3 lg:ml-2">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h2 className="text-xl font-poppins font-bold text-gray-800 mb-4">
                  How to Use the Face Scanning Feature?
                </h2>
                <div className="space-y-3">
                  <p className="text-gray-700 font-poppins">
                    <strong>Step 1:</strong> Register your face enrollment in the{' '}
                    <span className="font-semibold">Profile Page</span>. (If you've already
                    registered, proceed to the capture face)
                  </p>
                  <p className="text-gray-700 font-poppins">
                    <strong>Step 2:</strong> Position your face in the center of the camera frame
                  </p>
                  <p className="text-gray-700 font-poppins">
                    <strong>Step 3:</strong> Ensure proper lighting so your face is clearly visible
                  </p>
                  <p className="text-gray-700 font-poppins">
                    <strong>Step 4:</strong> Click{' '}
                    <span className="font-semibold">"Capture Face"</span> to take a photo
                  </p>
                  <p className="text-gray-700 font-poppins">
                    <strong>Step 5:</strong> Wait for the system to verify your identity
                  </p>
                  <p className="text-gray-700 font-poppins bg-yellow-100 p-2 rounded-lg">
                    <strong>Note:</strong> If recognition fails, ensure your face is clearly visible
                    and try again
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Success Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Reservation Found</h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <IoClose size={24} />
              </button>
            </div>

            <div className="p-3">
              {guestDetails && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Guest Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    <div>
                      <p className="text-gray-700">
                        <strong className="text-gray-900">Name:</strong> {guestDetails.firstname}{' '}
                        {guestDetails.lastname}
                      </p>
                      <p className="text-gray-700">
                        <strong className="text-gray-900">Mobile no. :</strong>{' '}
                        {guestDetails.countrycode}
                        {guestDetails.phonenumber}
                      </p>
                      <p className="text-gray-700">
                        <strong className="text-gray-900">Email:</strong> {guestDetails.emailid}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {bookingData.length > 0 && (
                <div>
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-800 font-medium">{message}</p>
                    {bookingData[0].attention_needed && (
                      <p className="text-red-600 mt-2 font-medium">
                        ⚠️ {bookingData[0].attention_needed}
                      </p>
                    )}
                  </div>

                  <h2 className="text-xl font-bold text-gray-800 mb-2">
                    Your Reservations <span className="text-gray-800">({bookingData.length})</span>
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {bookingData.map((booking, index) => (
                      <div
                        key={index}
                        className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
                      >
                        {/* Card Content */}
                        <div>
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                            <div className="mb-4 md:mb-0">
                              <h3 className="font-bold text-lg text-gray-800">
                                {booking.room_type.name}
                              </h3>
                              <p className="text-gray-600">
                                Reservation Number : {booking.order_id}
                              </p>
                              {booking.room && (
                                <p className="text-gray-600">
                                  Room {booking.room.room_number || '(N/A)'}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-end">
                              <span
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  booking.booking_status.toLowerCase() === 'hard'
                                    ? 'bg-gray-100 text-gray-800'
                                    : booking.booking_status.toLowerCase() === 'checkedout'
                                      ? 'bg-gray-100 text-gray-800'
                                      : booking.booking_status.toLowerCase() === 'soft' ||
                                          booking.booking_status.toLowerCase() === 'confirmed'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {booking.booking_status.toLowerCase() === 'hard'
                                  ? 'Checked In'
                                  : booking.booking_status.toLowerCase() === 'checkedout'
                                    ? 'Checked Out'
                                    : booking.booking_status.toLowerCase() === 'soft'
                                      ? 'Reserved'
                                      : booking.booking_status}
                              </span>
                              <p className="text-sm text-gray-500 mt-1">
                                {booking.number_of_guests} guest
                                {booking.number_of_guests > 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm text-gray-500">Check-in</p>
                              <p className="font-medium">{formatDate(booking.checkin_date)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Check-out</p>
                              <p className="font-medium">{formatDate(booking.checkout_date)}</p>
                            </div>
                          </div>
                        </div>

                        {/* Button always at bottom */}
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex justify-end items-center">
                            <button
                              onClick={() => handleProceed(booking)}
                              className="px-4 py-2 bg-black text-white font-medium rounded-md hover:bg-gray-800 transition-colors flex items-center gap-2"
                            >
                              <span>Select Reservation</span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={resetForm}
                  className="px-6 py-2 bg-gray-200 text-gray-800 font-medium rounded-md hover:bg-gray-300 transition-colors"
                >
                  Scan Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">{getErrorTitle()}</h2>
                <button onClick={closeErrorModal} className="text-gray-500 hover:text-gray-700">
                  <IoClose size={24} />
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-center mb-4">{getErrorIcon()}</div>
                <p className="text-gray-700 text-center">{errorMessage}</p>

                {/* Additional guidance based on error type */}
                {errorType === 'NO_RESERVATIONS' && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>What to do next:</strong>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Confirm you have a valid reservation</li>
                        <li>Check if your reservation date is today</li>
                        <li>Contact reception for assistance</li>
                      </ul>
                    </p>
                  </div>
                )}

                {errorType === 'FACE_NOT_ENROLLED' && (
                  <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                    <p className="text-sm text-orange-700">
                      <strong>To use face recognition:</strong>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Go to your Profile page</li>
                        <li>Register your face under "Update Information"</li>
                        <li>Return here and try again</li>
                      </ul>
                    </p>
                  </div>
                )}

                {errorType === 'NO_FACE_DETECTED' && (
                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-yellow-700">
                      <strong>Tips for better face detection:</strong>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Ensure good lighting on your face</li>
                        <li>Remove sunglasses or hats</li>
                        <li>Look directly at the camera</li>
                        <li>Make sure your entire face is visible</li>
                      </ul>
                    </p>
                  </div>
                )}

                {errorType === 'NETWORK_ERROR' && (
                  <div className="mt-4 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">
                      <strong>Connection issues:</strong>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Check your internet connection</li>
                        <li>Try switching between Wi-Fi and mobile data</li>
                        <li>Ensure you have a stable connection</li>
                      </ul>
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-center space-x-4">
                {errorType === 'FACE_NOT_ENROLLED' && (
                  <button
                    onClick={() => {
                      closeErrorModal();
                      navigate('/profile');
                    }}
                    className="px-6 py-2 bg-blue-500 text-white font-medium rounded-md hover:bg-blue-600 transition-colors"
                  >
                    Go to Profile
                  </button>
                )}
                <button
                  onClick={resetForm}
                  className={`px-6 py-2 ${getButtonColor()} text-white font-medium rounded-md transition-colors`}
                >
                  {errorType === 'NO_RESERVATIONS' ? 'Scan Again' : 'Try Again'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Early Check-in Disclaimer Modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h2 className="text-xl font-bold mb-4">Early Check-In Notice</h2>
            <p className="mb-4">
              You are attempting to check in before the standard check-in time of{' '}
              <strong>11:00 AM</strong>. Early check-in will be charged{' '}
              <strong>₹{earlyCheckinData?.extra_charge}</strong> and is subject to additional
              charges and room availability.
            </p>

            <p className="mb-6 font-semibold">Would you like to proceed with the early check-in?</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDisclaimer(false)}
                disabled={processing}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedBooking) {
                    proceedWithCheckIn(selectedBooking);
                  }
                }}
                disabled={processing}
                className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-Checkin Error Modal */}
      {showPreCheckinErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="relative bg-white rounded-lg shadow-lg max-w-[500px] w-full p-6 mx-4">
            <div className="text-center">
              <BsFillExclamationTriangleFill className="text-yellow-400 text-4xl mx-auto mb-3" />
              <h2 className="text-gray-900 text-lg font-semibold mb-2">Unable to Proceed</h2>
              <p className="text-red-600 text-md font-medium mb-3 whitespace-pre-line">
                {preCheckinErrorMessage}
              </p>
              <p className="text-gray-700 text-sm mb-4 mt-2">
                In case of any assistance please contact our reception support team at{' '}
                <a href={`mailto:${hotelInfo.email}`} className="text-blue-600 hover:underline">
                  {hotelInfo.name}
                </a>{' '}
                or call us at{' '}
                <a href={`mailto:${hotelInfo.email}`} className="text-blue-600 hover:underline">
                  {hotelInfo.email}
                </a>
                .
              </p>

              <button
                onClick={() => {
                  setShowPreCheckinErrorModal(false);
                  setShowModal(false);
                }}
                className="px-5 py-2 bg-black text-white rounded-md font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchReservation;

// import React, { useState, useRef } from "react";
// import Webcam from "react-webcam";
// import { useNavigate } from "react-router-dom";
// import { IoMdArrowRoundBack } from "react-icons/io";
// import { IoClose } from "react-icons/io5";
// import { BsFillExclamationTriangleFill } from "react-icons/bs";
// import Cookies from "js-cookie";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const getGuestIdFromToken = (token) => {
//   try {
//     const payload = JSON.parse(atob(token.split(".")[1]));
//     return payload.sub;
//   } catch (e) {
//     console.error("Error parsing token:", e);
//     return null;
//   }
// };

// const decryptUserData = async (encryptedData) => {
//   try {
//     if (encryptedData.email && encryptedData.email.length < 50) {
//       return encryptedData;
//     }

//     const accessToken = Cookies.get("access_token");
//     if (!accessToken) {
//       throw new Error("No access token found");
//     }

//     const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${accessToken}`,
//       },
//       body: JSON.stringify({
//         encrypted_fields: {
//           email: encryptedData.email,
//           phone_number: encryptedData.phone_number,
//           country_code: encryptedData.country_code,
//         },
//       }),
//     });

//     if (response.status === 401) {
//       return encryptedData;
//     }

//     if (!response.ok) {
//       throw new Error(`Decryption failed: ${response.statusText}`);
//     }

//     return await response.json();
//   } catch (error) {
//     console.error("Decryption error:", error);
//     return encryptedData;
//   }
// };

// const getGuestData = async () => {
//   const accessToken = Cookies.get("access_token");
//   if (!accessToken) return null;

//   try {
//     const guestId = Cookies.get("guest_id") || getGuestIdFromToken(accessToken);
//     if (!guestId) return null;

//     const profileResponse = await fetch(
//       `${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`,
//       {
//         method: "GET",
//         headers: {
//           Accept: "application/json",
//           Authorization: `Bearer ${accessToken}`,
//         },
//       }
//     );

//     if (!profileResponse.ok) {
//       throw new Error(`Profile API Error: ${profileResponse.statusText}`);
//     }

//     const profileData = await profileResponse.json();
//     const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

//     if (isDecrypted) {
//       return {
//         firstname: profileData.firstname || "",
//         lastname: profileData.lastname || "",
//         email: profileData.emailid || "",
//         phone: profileData.phonenumber || "",
//       };
//     }

//     const decryptedFields = await decryptUserData({
//       email: profileData.emailid,
//       phone_number: profileData.phonenumber,
//       country_code: profileData.countrycode,
//     });

//     return {
//       firstname: profileData.firstname || "",
//       lastname: profileData.lastname || "",
//       email: decryptedFields.email || profileData.emailid || "",
//       phone: decryptedFields.phone_number || profileData.phonenumber || "",
//     };
//   } catch (error) {
//     console.error("Error fetching guest data:", error);
//     return null;
//   }
// };

// const SearchReservation = () => {
//   const [preview, setPreview] = useState(null);
//   const [message, setMessage] = useState("");
//   const [similarity, setSimilarity] = useState(null);
//   const [guestId, setGuestId] = useState(null);
//   const [guestDetails, setGuestDetails] = useState(null);
//   const [bookingData, setBookingData] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [showErrorModal, setShowErrorModal] = useState(false);
//   const [errorMessage, setErrorMessage] = useState("");
//   const [showDisclaimer, setShowDisclaimer] = useState(false);
//   const [selectedBooking, setSelectedBooking] = useState(null);
//   const [processing, setProcessing] = useState(false);
//   const [earlyCheckinData, setEarlyCheckinData] = useState(null);
//   const [showPreCheckinErrorModal, setShowPreCheckinErrorModal] =
//     useState(false);
//   const [preCheckinErrorMessage, setPreCheckinErrorMessage] = useState("");
//   const [hotelInfo, setHotelInfo] = useState({ name: "", email: "" });
//   const webcamRef = useRef(null);
//   const navigate = useNavigate();

//   const shouldShowDisclaimer = (checkInDate) => {
//     const now = new Date();
//     const checkInDateTime = new Date(checkInDate);

//     // Set check-in time to 1:00 PM
//     checkInDateTime.setHours(12, 0, 0, 0);

//     // Calculate 2 hours prior to check-in time (11:00 AM)
//     const twoHoursPrior = new Date(checkInDateTime);
//     twoHoursPrior.setHours(twoHoursPrior.getHours() - 2);

//     // If current time is before the 2 hours prior to check-in, show disclaimer
//     return now < twoHoursPrior;
//   };

//   const captureImage = () => {
//     if (webcamRef.current && webcamRef.current.getScreenshot) {
//       const imageSrc = webcamRef.current.getScreenshot();
//       setPreview(imageSrc);
//       handleCheckIn(imageSrc);
//     } else {
//       setErrorMessage("Failed to capture image. Please try again.");
//       setShowErrorModal(true);
//     }
//   };

//   const validateFaceMatch = async (faceRecognitionData) => {
//     try {
//       // Get current user's data
//       const currentUserData = await getGuestData();
//       if (!currentUserData) {
//         throw new Error("Unable to verify your account. Please log in again.");
//       }

//       // Get email from face recognition result
//       const recognizedEmail = faceRecognitionData.bookings?.[0]?.guest_email;
//       if (!recognizedEmail) {
//         throw new Error("No email found in recognition data.");
//       }

//       // Compare emails
//       if (
//         currentUserData.email.toLowerCase() !== recognizedEmail.toLowerCase()
//       ) {
//         throw new Error(
//           "Face recognition matched a different account. Please use the account that made this reservation."
//         );
//       }

//       return true;
//     } catch (error) {
//       console.error("Validation error:", error);
//       throw error; // Re-throw to handle in calling function
//     }
//   };

//   const handleCheckIn = async (imageData) => {
//     try {
//       const base64Response = await fetch(imageData);
//       const blob = await base64Response.blob();
//       const formData = new FormData();
//       formData.append("file", blob, "captured_image.jpg");

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/checkin`, {
//         method: "POST",
//         headers: { accept: "application/json" },
//         body: formData,
//       });

//       if (response.ok) {
//         const data = await response.json();
//         if (data.bookings && data.bookings.length > 0) {
//           // Validate the recognized face matches logged-in user
//           await validateFaceMatch(data);

//           // Extract guest details from the first booking
//           const firstBooking = data.bookings[0];
//           const guestDetails = {
//             firstname: firstBooking.guest_name.split(" ")[0],
//             lastname: firstBooking.guest_name.split(" ").slice(1).join(" "),
//             emailid: firstBooking.guest_email,
//             phonenumber: firstBooking.guest_phone,
//           };

//           setMessage(data.message);
//           setGuestDetails(guestDetails);
//           setBookingData(data.bookings);
//           setShowModal(true);
//         } else {
//           setErrorMessage(
//             data.message || "No booking data found. Please try again."
//           );
//           setShowErrorModal(true);
//         }
//       } else {
//         const errorData = await response.json();
//         setErrorMessage(
//           errorData.message || "Face verification failed. Please try again."
//         );
//         setShowErrorModal(true);
//       }
//     } catch (error) {
//       console.error("Error:", error);
//       setErrorMessage(
//         error.message || "An unexpected error occurred. Please try again."
//       );
//       setShowErrorModal(true);
//     }
//   };

//   const proceedWithCheckIn = async (booking) => {
//     setProcessing(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/pre-checkin/`, {
//         method: "POST",
//         headers: {
//           accept: "application/json",
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({
//           order_id: booking.order_id,
//           last_name: guestDetails.lastname,
//           email: guestDetails.emailid,
//         }),
//       });

//       if (response.ok) {
//         const data = await response.json();

//         // Check if the API response indicates failure
//         if (data.success === false) {
//           setPreCheckinErrorMessage(
//             data.message || "Check-in failed. Please try again."
//           );

//           // Get hotel info from localStorage
//           const hotelPropertyCache = JSON.parse(
//             localStorage.getItem("hotelPropertyCache") || "{}"
//           );
//           if (hotelPropertyCache.data) {
//             setHotelInfo({
//               name:
//                 hotelPropertyCache.data.propertyname ||
//                 hotelPropertyCache.data.name ||
//                 "The Hotel",
//               email: hotelPropertyCache.data.email || "support@hotel.com",
//             });
//           }

//           setShowPreCheckinErrorModal(true);
//           setProcessing(false);
//           return;
//         }

//         // Prepare the full booking details object in the required format
//         // Maintain the original structure that other components expect
//         const bookingDetails = {
//           success: true,
//           message: data.message,
//           today: new Date().toLocaleDateString("en-US", {
//             weekday: "long",
//             year: "numeric",
//             month: "long",
//             day: "numeric",
//           }),
//           order_id: data.order_id,
//           primary_guest: data.primary_guest,
//           bookings: data.bookings,
//           multiple_rooms: data.multiple_rooms,
//           attention_needed: data.attention_needed,
//           primaryBooking: data.bookings[0], // Use the first booking from API response
//         };

//         // Store the hotel property cache
//         const hotelPropertyCache = {
//           data: {
//             propertyname: data.bookings[0].room_type.property.name,
//             city: "Chiplun",
//             name: data.bookings[0].room_type.property.name,
//             location: "Taluka Khed Chiplun Area",
//             address: "Taluka Khed Chiplun Area, Lote, Maharashtra, 415722",
//             phone: "086987 32336",
//             email: "tushar.bhosle@hotelpagoda.com",
//             propertyid: data.bookings[0].room_type.property.id,
//             logo_url:
//               "https://storage.googleapis.com/hms-image-storage/property-logos/1/176327d44-a199-41b1-9c66.png",
//             homepage_url:
//               "https://storage.googleapis.com/hms-image-storage/property-homepage/1/f102d3be-1d32-48c6-9f89-a201c0c101b5.jpg",
//             homepage_video_url: "",
//           },
//           timestamp: Date.now(),
//         };

//         // Save to storage
//         localStorage.setItem("bookingDetails", JSON.stringify(bookingDetails));
//         sessionStorage.setItem(
//           "bookingDetails",
//           JSON.stringify(bookingDetails)
//         );
//         localStorage.setItem(
//           "hotelPropertyCache",
//           JSON.stringify(hotelPropertyCache)
//         );

//         // Navigate based on booking status
//         if (data.bookings[0].booking_status.toLowerCase() === "soft") {
//           navigate("/checkin-details");
//         } else {
//           navigate("/reservation/options/booking/welcome");
//         }
//       } else {
//         const errorData = await response.json();
//         setPreCheckinErrorMessage(
//           errorData.message ||
//           errorData.detail?.message ||
//           "Check-in failed. Please try again."
//         );

//         // Get hotel info from localStorage
//         const hotelPropertyCache = JSON.parse(
//           localStorage.getItem("hotelPropertyCache") || "{}"
//         );
//         if (hotelPropertyCache.data) {
//           setHotelInfo({
//             name:
//               hotelPropertyCache.data.propertyname ||
//               hotelPropertyCache.data.name ||
//               "The Hotel",
//             email: hotelPropertyCache.data.email || "support@hotel.com",
//           });
//         }

//         setShowPreCheckinErrorModal(true);
//       }
//     } catch (error) {
//       console.error("Error:", error);
//       setPreCheckinErrorMessage(
//         error.message || "An unexpected error occurred. Please try again."
//       );

//       // Get hotel info from localStorage
//       const hotelPropertyCache = JSON.parse(
//         localStorage.getItem("hotelPropertyCache") || "{}"
//       );
//       if (hotelPropertyCache.data) {
//         setHotelInfo({
//           name:
//             hotelPropertyCache.data.propertyname ||
//             hotelPropertyCache.data.name ||
//             "The Hotel",
//           email: hotelPropertyCache.data.email || "support@hotel.com",
//         });
//       }

//       setShowPreCheckinErrorModal(true);
//     } finally {
//       setProcessing(false);
//       setShowDisclaimer(false);
//     }
//   };

//   const handleProceed = async (booking) => {
//     setSelectedBooking(booking);

//     // Check if already checked in - don't show disclaimer for checked-in guests
//     if (booking.booking_status.toLowerCase() === "hard") {
//       // Skip disclaimer and proceed directly for already checked-in guests
//       proceedWithCheckIn(booking);
//       return;
//     }

//     // Check if we need to show the early check-in disclaimer
//     const today = new Date().toISOString().split("T")[0];
//     if (
//       booking.checkin_date === today &&
//       shouldShowDisclaimer(booking.checkin_date)
//     ) {
//       try {
//         // Fetch early check-in details
//         const response = await fetch(
//           `${BQ_BASE_URL}/bq/api/pre-checkin/?order_id=${booking.order_id}&last_name=${guestDetails.lastname}`,
//           {
//             method: "GET",
//             headers: {
//               accept: "application/json",
//             },
//           }
//         );

//         if (response.ok) {
//           const data = await response.json();
//           // Extract early check-in data from the first booking
//           if (data.bookings && data.bookings.length > 0) {
//             setEarlyCheckinData(data.bookings[0].early_checkin);
//           }
//         }
//       } catch (error) {
//         console.error("Error fetching early check-in details:", error);
//       }

//       setShowDisclaimer(true);
//     } else {
//       // Proceed directly if no disclaimer needed
//       proceedWithCheckIn(booking);
//     }
//   };

//   const resetForm = () => {
//     setPreview(null);
//     setMessage("");
//     setSimilarity(null);
//     setGuestId(null);
//     setGuestDetails(null);
//     setBookingData([]);
//     setShowModal(false);
//     setShowErrorModal(false);
//     setShowDisclaimer(false);
//     setEarlyCheckinData(null);
//     setShowPreCheckinErrorModal(false);
//     setPreCheckinErrorMessage("");
//   };

//   const closeModal = () => {
//     setShowModal(false);
//   };

//   const closeErrorModal = () => {
//     setShowErrorModal(false);
//   };

// const formatDate = (dateString) => {
//   const date = new Date(dateString);
//   const day = String(date.getDate()).padStart(2, "0");
//   const month = String(date.getMonth() + 1).padStart(2, "0");
//   const year = String(date.getFullYear()).slice(-2);
//   return `${day}/${month}/${year}`;
// };

//   return (
//     <div className="min-h-1 flex flex-col items-center p-6 bg-gray-50">
//       <div className="w-full max-w-6xl">
//         <h1 className="text-2xl font-poppins font-bold text-center text-gray-800 mb-4">
//           Search Reservation
//         </h1>
//         <p className="text-center font-poppins text-gray-600 mb-8 text-lg font-semibold">
//           Scan your face to retrieve your reservation details
//         </p>

//         <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between">
//           {/* Webcam and Capture Section */}
//           <div className="w-full lg:w-1/2 flex flex-col items-center mb-8 lg:mb-0">
//             <div className="bg-white p-4 rounded-lg shadow-lg">
//               {preview ? (
//                 <img
//                   src={preview}
//                   alt="Preview"
//                   className="rounded-lg w-96 h-80 object-cover"
//                 />
//               ) : (
//                 <Webcam
//                   audio={false}
//                   ref={webcamRef}
//                   screenshotFormat="image/jpeg"
//                   className="rounded-lg w-96 h-80 object-cover"
//                 />
//               )}
//             </div>
//             <div className="flex gap-3 mt-4">
//               <button
//                 onClick={captureImage}
//                 className="px-8 py-3 font-poppins bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition duration-300"
//               >
//                 Capture Face
//               </button>
//               <button
//                 onClick={resetForm}
//                 className="px-8 py-3 font-poppins bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition duration-300"
//               >
//                 Try Again
//               </button>
//             </div>
//           </div>

//           {/* Instructions Section */}
//           {!showModal && !showErrorModal && (
//             <div className="w-full lg:w-2/3 lg:ml-2">
//               <div className="bg-white p-6 rounded-lg shadow-lg">
//                 <h2 className="text-xl font-poppins font-bold text-gray-800 mb-4">
//                   How to Use the Face Scanning Feature?
//                 </h2>
//                 <div className="space-y-3">
//                   <p className="text-gray-700 font-poppins">
//                     <strong>Step 1:</strong> Register your face enrollment in the{" "}
//                     <span className="font-semibold">Profile Page</span>.
//                     (If you’ve already registered, proceed to the capture face)
//                   </p>
//                   <p className="text-gray-700 font-poppins">
//                     <strong>Step 2:</strong> Position your face in the center of the camera frame
//                   </p>
//                   <p className="text-gray-700 font-poppins">
//                     <strong>Step 3:</strong> Ensure proper lighting so your face is clearly visible
//                   </p>
//                   <p className="text-gray-700 font-poppins">
//                     <strong>Step 4:</strong> Click{" "}
//                     <span className="font-semibold">"Capture Face"</span> to take a photo
//                   </p>
//                   <p className="text-gray-700 font-poppins">
//                     <strong>Step 5:</strong> Wait for the system to verify your identity
//                   </p>
//                   <p className="text-gray-700 font-poppins bg-yellow-100 p-2 rounded-lg">
//                     <strong>Note:</strong> If recognition fails, ensure your face is clearly visible and try again
//                   </p>
//                 </div>
//               </div>

//             </div>
//           )}
//         </div>
//       </div>

//       {/* Success Modal */}
//       {showModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[80vh] overflow-y-auto">
//             <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
//               <h2 className="text-xl font-bold text-gray-800">
//                 Reservation Found
//               </h2>
//               <button
//                 onClick={closeModal}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 <IoClose size={24} />
//               </button>
//             </div>

//             <div className="p-3">
//               {guestDetails && (
//                 <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
//                   <h2 className="text-xl font-bold text-gray-800 mb-4">
//                     Guest Information
//                   </h2>
//                   <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
//                     <div>
//                       <p className="text-gray-700">
//                         <strong className="text-gray-900">Name:</strong>{" "}
//                         {guestDetails.firstname} {guestDetails.lastname}
//                       </p>
//                       <p className="text-gray-700">
//                         <strong className="text-gray-900">Mobile no. :</strong>{" "}
//                         {guestDetails.countrycode}
//                         {guestDetails.phonenumber}
//                       </p>
//                       <p className="text-gray-700">
//                         <strong className="text-gray-900">Email:</strong>{" "}
//                         {guestDetails.emailid}
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//               )}

//               {bookingData.length > 0 && (
//                 <div>
//                   <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
//                     <p className="text-gray-800 font-medium">{message}</p>
//                     {bookingData[0].attention_needed && (
//                       <p className="text-red-600 mt-2 font-medium">
//                         ⚠️ {bookingData[0].attention_needed}
//                       </p>
//                     )}
//                   </div>

//                   <h2 className="text-xl font-bold text-gray-800 mb-2">
//                     Your Reservations{" "}
//                     <span className="text-gray-800">
//                       ({bookingData.length})
//                     </span>
//                   </h2>

//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                     {bookingData.map((booking, index) => (
//                       <div
//                         key={index}
//                         className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
//                       >
//                         {/* Card Content */}
//                         <div>
//                           <div className="flex flex-col md:flex-row md:justify-between md:items-start">
//                             <div className="mb-4 md:mb-0">
//                               <h3 className="font-bold text-lg text-gray-800">
//                                 {booking.room_type.name}
//                               </h3>
//                               <p className="text-gray-600">
//                                 Reservation Number : {booking.order_id}
//                               </p>
//                               {booking.room && (
//                                 <p className="text-gray-600">
//                                   Room {booking.room.room_number}
//                                 </p>
//                               )}
//                             </div>
//                             <div className="flex flex-col items-end">
//                               <span
//                                 className={`px-2 py-1 rounded text-xs font-medium ${booking.booking_status.toLowerCase() ===
//                                     "hard"
//                                     ? "bg-gray-100 text-gray-800"
//                                     : booking.booking_status.toLowerCase() ===
//                                       "checkedout"
//                                       ? "bg-gray-100 text-gray-800"
//                                       : booking.booking_status.toLowerCase() ===
//                                         "soft" ||
//                                         booking.booking_status.toLowerCase() ===
//                                         "confirmed"
//                                         ? "bg-green-100 text-green-800"
//                                         : "bg-yellow-100 text-yellow-800"
//                                   }`}
//                               >
//                                 {booking.booking_status.toLowerCase() === "hard"
//                                   ? "Checked In"
//                                   : booking.booking_status.toLowerCase() ===
//                                     "checkedout"
//                                     ? "Checked Out"
//                                     : booking.booking_status.toLowerCase() ===
//                                       "soft"
//                                       ? "Reserved"
//                                       : booking.booking_status}
//                               </span>
//                               <p className="text-sm text-gray-500 mt-1">
//                                 {booking.number_of_guests} guest
//                                 {booking.number_of_guests > 1 ? "s" : ""}
//                               </p>
//                             </div>
//                           </div>

//                           <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
//                             <div>
//                               <p className="text-sm text-gray-500">Check-in</p>
//                               <p className="font-medium">
//                                 {formatDate(booking.checkin_date)}
//                               </p>
//                             </div>
//                             <div>
//                               <p className="text-sm text-gray-500">Check-out</p>
//                               <p className="font-medium">
//                                 {formatDate(booking.checkout_date)}
//                               </p>
//                             </div>
//                           </div>
//                         </div>

//                         {/* Button always at bottom */}
//                         <div className="mt-4 pt-4 border-t border-gray-100">
//                           <div className="flex justify-end items-center">
//                             <button
//                               onClick={() => handleProceed(booking)}
//                               className="px-4 py-2 bg-black text-white font-medium rounded-md hover:bg-gray-800 transition-colors flex items-center gap-2"
//                             >
//                               <span>Select Reservation</span>
//                               <svg
//                                 xmlns="http://www.w3.org/2000/svg"
//                                 className="h-4 w-4"
//                                 fill="none"
//                                 viewBox="0 0 24 24"
//                                 stroke="currentColor"
//                               >
//                                 <path
//                                   strokeLinecap="round"
//                                   strokeLinejoin="round"
//                                   strokeWidth={2}
//                                   d="M9 5l7 7-7 7"
//                                 />
//                               </svg>
//                             </button>
//                           </div>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               <div className="mt-6 flex justify-end">
//                 <button
//                   onClick={resetForm}
//                   className="px-6 py-2 bg-gray-200 text-gray-800 font-medium rounded-md hover:bg-gray-300 transition-colors"
//                 >
//                   Scan Again
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Error Modal */}
//       {showErrorModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
//             <div className="p-6">
//               <div className="flex justify-between items-center mb-4">
//                 <h2 className="text-xl font-bold text-red-600">
//                   Verification Failed
//                 </h2>
//                 <button
//                   onClick={closeErrorModal}
//                   className="text-gray-500 hover:text-gray-700"
//                 >
//                   <IoClose size={24} />
//                 </button>
//               </div>

//               <div className="mb-6">
//                 <div className="flex items-center justify-center mb-4">
//                   <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     className="h-16 w-16 text-red-500"
//                     fill="none"
//                     viewBox="0 0 24 24"
//                     stroke="currentColor"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
//                     />
//                   </svg>
//                 </div>
//                 <p className="text-gray-700 text-center">{errorMessage}</p>
//               </div>

//               <div className="flex justify-center">
//                 <button
//                   onClick={resetForm}
//                   className="px-6 py-2 bg-red-500 text-white font-medium rounded-md hover:bg-red-600 transition-colors"
//                 >
//                   Try Again
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Early Check-in Disclaimer Modal */}
//       {showDisclaimer && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg max-w-lg w-full p-6">
//             <h2 className="text-xl font-bold mb-4">Early Check-In Notice</h2>
//             <p className="mb-4">
//               You are attempting to check in before the standard check-in time
//               of <strong>1:00 PM</strong>. Early check-in will be charged{" "}
//               <strong>₹{earlyCheckinData?.extra_charge}</strong> and is subject
//               to additional charges and room availability.
//             </p>

//             <p className="mb-6 font-semibold">
//               Would you like to proceed with the early check-in?
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={() => setShowDisclaimer(false)}
//                 disabled={processing}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={() => {
//                   if (selectedBooking) {
//                     proceedWithCheckIn(selectedBooking);
//                   }
//                 }}
//                 disabled={processing}
//                 className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
//               >
//                 {processing ? "Processing..." : "Proceed"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Pre-Checkin Error Modal */}
//       {showPreCheckinErrorModal && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
//           <div className="relative bg-white rounded-lg shadow-lg max-w-[500px] w-full p-6 mx-4">
//             <div className="text-center">
//               <BsFillExclamationTriangleFill className="text-yellow-400 text-4xl mx-auto mb-3" />
//               <h2 className="text-gray-900 text-lg font-semibold mb-2">
//                 Unable to Proceed
//               </h2>
//               <p className="text-red-600 text-md font-medium mb-3 whitespace-pre-line">
//                 {preCheckinErrorMessage}
//               </p>
//               <p className="text-gray-700 text-sm mb-4 mt-2">
//                 In case of any assistance please contact our reception support
//                 team at{" "}
//                 <a
//                   href={`mailto:${hotelInfo.email}`}
//                   className="text-blue-600 hover:underline"
//                 >
//                   {hotelInfo.name}
//                 </a>{" "}
//                 or call us at{" "}
//                 <a
//                   href={`mailto:${hotelInfo.email}`}
//                   className="text-blue-600 hover:underline"
//                 >
//                   {hotelInfo.email}
//                 </a>
//                 .
//               </p>

//               <button
//                 onClick={() => {
//                   setShowPreCheckinErrorModal(false);
//                   setShowModal(false);
//                 }}
//                 className="px-5 py-2 bg-black text-white rounded-md font-medium"
//               >
//                 Try Again
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default SearchReservation;
